import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Screen } from '@/components/layout/Screen';
import { useQuery } from '@tanstack/react-query';
import { getSignedAudioUrl } from '@/features/audio/signedAudio';
import { getAuthenticatedClient, getCurrentUserId } from '@/services/client';
import { edgeFunctions } from '@/services/edgeFunctions';
import { format } from 'date-fns';
import { Play, Volume2, ThumbsUp, ThumbsDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export const ConversationDetailScreen: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const { t, i18n } = useTranslation();
  const currentUserId = getCurrentUserId();
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [expandedQuestion, setExpandedQuestion] = useState(false);
  const [expandedAnswer, setExpandedAnswer] = useState(false);

  const { data: conversation } = useQuery({
    queryKey: ['voice_conversation_detail', conversationId],
    queryFn: async () => {
      if (!conversationId || !currentUserId) return null;
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from('voice_conversations')
        .select('*')
        .eq('id', conversationId)
        .eq('user_id', currentUserId)
        .maybeSingle();
      if (error) {
        console.error('Failed to fetch conversation:', error);
        return null;
      }
      return data;
    },
    enabled: !!conversationId && !!currentUserId,
    staleTime: 1000 * 60 * 5,
  });

  const generateTtsAudio = async (text: string): Promise<string | null> => {
    try {
      const result = await edgeFunctions.textToSpeech(text, i18n.language || 'bn');
      if (!result?.data?.audio_base64) return null;
      const binary = atob(result.data.audio_base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: result.data.content_type || 'audio/mp3' });
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  };

  const handleTapToHear = async (id: string, content: string, audioUrl?: string | null) => {
    // Toggle off if already playing
    if (playingId === id) {
      setPlayingId(null);
      window.speechSynthesis?.cancel();
      return;
    }

    setPlayingId(id);

    // Tier 1: Use stored audio URL if available (from voice_conversations.response_audio_url)
    if (audioUrl) {
      // voice-messages is a PRIVATE bucket (FIX-CCS-AUDIO-PRIVACY): a bare PATH is signed on read;
      // a full http URL (public voice-responses reply) plays as-is.
      let src = audioUrl;
      if (!/^https?:\/\//i.test(audioUrl)) {
        const signed = await getSignedAudioUrl(audioUrl, 'voice-messages');
        if (!signed) { setPlayingId(null); return; }
        src = signed;
      }
      const audio = new Audio(src);
      audio.onended = () => setPlayingId(null);
      audio.onerror = () => setPlayingId(null);
      audio.play().catch(() => setPlayingId(null));
      return;
    }

    // Tier 2: Generate server-side TTS on demand
    const generatedUrl = await generateTtsAudio(content);
    if (generatedUrl) {
      const audio = new Audio(generatedUrl);
      audio.onended = () => setPlayingId(null);
      audio.onerror = () => setPlayingId(null);
      audio.play().catch(() => setPlayingId(null));
      return;
    }

    // Tier 3: Browser speechSynthesis fallback with lang set
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(content);
      utterance.lang = i18n.language || 'bn';
      utterance.rate = 0.9;
      utterance.onend = () => setPlayingId(null);
      window.speechSynthesis.speak(utterance);
    } else {
      setPlayingId(null);
    }
  };

  const handleFeedback = async (helpful: boolean) => {
    if (!conversationId) return;
    try {
      const client = getAuthenticatedClient();
      await client
        .from('voice_conversations')
        .update({ was_helpful: helpful, feedback_at: new Date().toISOString() })
        .eq('id', conversationId);
      toast.success(helpful ? `👍 ${t("agent.feedbackThanks", { defaultValue: "Thanks for your feedback!" })}` : `👎 ${t("agent.feedbackThanksImprove", { defaultValue: "Thanks, we'll improve!" })}`);
    } catch {
      toast.error(t("agent.feedbackFailed", { defaultValue: "Failed to save feedback" }));
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!conversation) {
    return (
      <Screen showTopBar topBarProps={{ title: t("agent.conversation", { defaultValue: "Conversation" }), showMapPin: false, showSettings: false }} showBottomNav={false}>
        <div className="flex items-center justify-center h-full">
          <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
        </div>
      </Screen>
    );
  }

  return (
    <Screen
      showTopBar
      topBarProps={{ title: t("agent.conversation", { defaultValue: "Conversation" }), showMapPin: false, showSettings: false }}
      showBottomNav={false}
      className="flex flex-col bg-background"
    >
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="border border-border rounded-2xl bg-card overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
            <span className="text-sm text-muted-foreground">
              {conversation.created_at ? format(new Date(conversation.created_at), 'dd/MM/yyyy') : '—'}
            </span>
            <span className="text-xs font-medium text-success bg-success/10 px-2 py-0.5 rounded-full">
              {t("agent.ready", { defaultValue: "Ready!" })}
            </span>
          </div>

          {/* Your question */}
          <div className="px-4 py-3 border-b border-border/50">
            <p className="text-xs font-medium text-muted-foreground mb-2">{t("agent.yourQuestion", { defaultValue: "Your question" })}</p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleTapToHear('q', conversation.user_transcription || '', conversation.user_audio_url)}
                className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0"
              >
                <Play className="w-5 h-5 text-success ml-0.5" />
              </button>
              <div className="flex-1">
                <div className="flex items-center gap-0.5 h-6">
                  {[...Array(24)].map((_, i) => (
                    <div
                      key={i}
                      className="w-1 bg-success/40 rounded-full"
                      style={{ height: `${Math.sin(i * 0.5) * 12 + 10}px` }}
                    />
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">
                  {formatDuration(conversation.user_audio_duration_seconds)}
                </span>
              </div>
            </div>
            {conversation.user_transcription && (
              <div className="mt-2">
                <p className={cn("text-[13px] text-muted-foreground leading-relaxed", !expandedQuestion && "line-clamp-2")}>
                  {conversation.user_transcription}
                </p>
                {conversation.user_transcription.length > 100 && (
                  <button onClick={() => setExpandedQuestion(!expandedQuestion)} className="text-xs text-primary mt-1">
                    {expandedQuestion ? t("common.showLess") : t("common.showMore")}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* AgriAgent answer */}
          <div className="px-4 py-3 border-b border-border/50">
            <p className="text-xs font-medium text-muted-foreground mb-2">{t("agent.agentAnswer", { defaultValue: "AgriAgent answer" })}</p>
            <button
              onClick={() => conversation.response_text && handleTapToHear('a', conversation.response_text, conversation.response_audio_url)}
              className="flex items-center gap-3 w-full text-left"
            >
              <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0">
                <Volume2 className="w-5 h-5 text-success" />
              </div>
              <span className="text-sm text-success font-medium">
                {playingId === 'a' ? t("agent.playing", { defaultValue: "Playing..." }) : t("agent.tapToListen", { defaultValue: "Tap to listen" })}
              </span>
            </button>
            {conversation.response_text && (
              <div className="mt-2">
                <p className={cn("text-[13px] text-foreground/80 leading-relaxed", !expandedAnswer && "line-clamp-4")}>
                  {conversation.response_text}
                </p>
                {conversation.response_text.length > 200 && (
                  <button onClick={() => setExpandedAnswer(!expandedAnswer)} className="text-xs text-primary mt-1">
                    {expandedAnswer ? t("common.showLess") : t("common.showMore")}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Feedback */}
          <div className="px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{t("agent.wasThisHelpful", { defaultValue: "Was this helpful?" })}</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleFeedback(true)}
                className={cn(
                  "w-9 h-9 rounded-full flex items-center justify-center transition-colors",
                  conversation.was_helpful === true ? "bg-success/20 text-success" : "bg-muted text-muted-foreground hover:bg-success/10"
                )}
              >
                <ThumbsUp className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleFeedback(false)}
                className={cn(
                  "w-9 h-9 rounded-full flex items-center justify-center transition-colors",
                  conversation.was_helpful === false ? "bg-destructive/20 text-destructive" : "bg-muted text-muted-foreground hover:bg-destructive/10"
                )}
              >
                <ThumbsDown className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Screen>
  );
};
